import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, BellRing, X, ExternalLink, Info, AlertTriangle, MessageSquare, Megaphone, Trash2, Check } from 'lucide-react';
import { useNotifications } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';

const NotificationBell = ({ iconClassName }) => {
    const { notifications, unreadCount, markAsRead } = useNotifications();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);

    if (user?.notificationsEnabled === false) return null;

    const handleNotificationClick = (notification) => {
        markAsRead(notification.id);
        navigate(notification.link);
        setIsOpen(false);
    };

    const getIcon = (type) => {
        switch (type) {
            case 'PUNISHMENT': return <AlertTriangle className="h-4 w-4 text-red-500" />;
            case 'TICKET_REPLY': return <MessageSquare className="h-4 w-4 text-blue-500" />;
            case 'TICKET_STATUS': return <Info className="h-4 w-4 text-yellow-500" />;
            case 'ANNOUNCEMENT': return <Megaphone className="h-4 w-4 text-purple-500" />;
            default: return <Bell className="h-4 w-4 text-gray-500" />;
        }
    };

    return (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <DropdownMenuTrigger asChild>
                <button className="relative p-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 transition-all group focus:outline-none">
                    <AnimatePresence mode="wait">
                        {unreadCount > 0 ? (
                            <motion.div
                                key="ringing"
                                animate={{
                                    rotate: [0, 15, -15, 10, -10, 0],
                                }}
                                transition={{
                                    repeat: Infinity,
                                    duration: 2,
                                    repeatDelay: 3
                                }}
                            >
                                <BellRing className={`h-6 w-6 transition-colors text-yellow-500 animate-pulse drop-shadow-[0_0_8px_rgba(234,179,8,0.5)]`} />
                            </motion.div>
                        ) : (
                            <motion.div key="normal">
                                <Bell className={`h-6 w-6 transition-colors ${iconClassName || 'text-gray-400 group-hover:text-gray-300'}`} />
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {unreadCount > 0 && (
                        <motion.span
                            initial={{ scale: 0 }}
                            animate={{ scale: [1, 1.2, 1] }}
                            transition={{ repeat: Infinity, duration: 2 }}
                            className="absolute -top-2 -right-2 h-6 w-6 bg-gradient-to-r from-red-600 to-pink-600 text-white text-xs font-bold rounded-full flex items-center justify-center border-2 border-[#09090b] shadow-lg shadow-red-500/20"
                        >
                            {unreadCount > 9 ? '9+' : unreadCount}
                        </motion.span>
                    )}
                </button>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="start" className="w-[400px] p-0 bg-black/80 backdrop-blur-xl border-white/10 text-white overflow-hidden rounded-3xl shadow-2xl z-[100] mt-2">
                <div className="p-5 border-b border-white/10 flex items-center justify-between bg-gradient-to-r from-white/5 to-transparent">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-lg">
                            <BellRing className="h-5 w-5 text-blue-400" />
                        </div>
                        <div>
                            <h3 className="font-bold text-base">Notificações</h3>
                            <p className="text-xs text-gray-400">Suas atualizações recentes</p>
                        </div>
                    </div>
                    {unreadCount > 0 && (
                        <Badge className="bg-gradient-to-r from-blue-600 to-purple-600 border-none shadow-lg shadow-blue-500/20">
                            {unreadCount} nova{unreadCount !== 1 ? 's' : ''}
                        </Badge>
                    )}
                </div>

                <ScrollArea className="h-[400px]">
                    {notifications.length > 0 ? (
                        <div className="p-3 space-y-2">
                            {notifications.map((notification) => (
                                <motion.div
                                    key={notification.id}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    className={`group p-4 rounded-2xl cursor-pointer transition-all border relative overflow-hidden ${notification.read ? 'bg-transparent border-transparent opacity-60 hover:bg-white/5' : 'bg-gradient-to-br from-white/5 to-white/[0.02] border-white/10 hover:border-white/20 shadow-lg'}`}
                                    onClick={() => handleNotificationClick(notification)}
                                >
                                    <div className={`absolute inset-0 bg-gradient-to-r ${notification.type === 'PUNISHMENT' ? 'from-red-500/5' : notification.type === 'ANNOUNCEMENT' ? 'from-purple-500/5' : 'from-blue-500/5'} to-transparent opacity-0 group-hover:opacity-100 transition-opacity`} />

                                    <div className="flex gap-4 relative z-10">
                                        <div className={`mt-1 p-3 rounded-2xl ${notification.read ? 'bg-gray-800/50' : 'bg-white/5 ring-1 ring-white/10'} shrink-0 h-fit`}>
                                            {getIcon(notification.type)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex justify-between items-start gap-2 mb-1">
                                                <h4 className={`text-sm font-bold truncate ${notification.read ? 'text-gray-400' : 'text-white group-hover:text-blue-300 transition-colors'}`}>
                                                    {notification.title}
                                                </h4>
                                                <span className="text-[10px] font-medium text-gray-500 bg-white/5 px-2 py-0.5 rounded-full whitespace-nowrap">
                                                    {new Date(notification.date).toLocaleDateString('pt-BR', { hour: '2-digit', minute: '2-digit' }).split(' ')[1]}
                                                </span>
                                            </div>
                                            <p className={`text-xs leading-relaxed ${notification.read ? 'text-gray-500' : 'text-gray-300'}`}>
                                                {notification.message}
                                            </p>
                                            <div className="flex items-center gap-3 mt-3">
                                                {!notification.read && (
                                                    <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-blue-400 opacity-60 group-hover:opacity-100 transition-opacity">
                                                        <span>Visualizar</span>
                                                        <ExternalLink className="h-3 w-3" />
                                                    </div>
                                                )}
                                                {!notification.read && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            markAsRead(notification.id);
                                                        }}
                                                        className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-green-400 opacity-60 group-hover:opacity-100 transition-opacity hover:text-green-300"
                                                    >
                                                        <span>Marcar como lida</span>
                                                        <Check className="h-3 w-3" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-full py-16 px-6 text-center">
                            <div className="relative mb-6">
                                <div className="absolute inset-0 bg-blue-500/20 blur-xl rounded-full" />
                                <div className="relative p-6 bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl border border-white/10">
                                    <Bell className="h-10 w-10 text-gray-500" />
                                </div>
                            </div>
                            <h3 className="text-lg font-bold text-white mb-2">Tudo tranquilo!</h3>
                            <p className="text-sm text-gray-400 max-w-[200px]">Você está em dia com todas as suas notificações.</p>
                        </div>
                    )}
                </ScrollArea>

                <div className="p-4 border-t border-white/10 bg-black/20 backdrop-blur-sm">
                    <Button
                        variant="ghost"
                        className="w-full bg-white/5 hover:bg-white/10 text-gray-300 hover:text-white border border-white/5 rounded-xl h-10 text-xs font-medium transition-all"
                        onClick={() => navigate('/settings')}
                    >
                        Gerenciar Preferências
                    </Button>
                </div>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export default NotificationBell;
